import { useEffect, useRef } from 'react';

type ShortcutHandler = (event: KeyboardEvent) => void;

/** Maps a `KeyboardEvent.key` (e.g. `ArrowLeft`, `b`) to the action it triggers. */
type ShortcutMap = Record<string, ShortcutHandler>;

const isTypingTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/**
 * Binds a key → handler dispatch table to the `window` keydown event.
 *
 * The listener attaches once and reads the latest map/enabled flag through refs,
 * so passing a fresh object every render never re-subscribes. Lookup falls back
 * to the lower-cased key, so `b` also matches `Shift+B`. Shortcuts are ignored
 * while the user is typing in a field, and while `enabled` is false.
 */
export const useKeyboardShortcuts = (shortcuts: ShortcutMap, enabled = true): void => {
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!enabledRef.current || isTypingTarget(event.target)) return;

      const handler = shortcutsRef.current[event.key] ?? shortcutsRef.current[event.key.toLowerCase()];
      if (!handler) return;

      event.preventDefault();
      handler(event);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
};
